import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthShell } from "@/pages/Login";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const { applySession } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setError("This invitation link is missing its token.");
      return;
    }
    api
      .get(`/api/auth/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(apiError(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/api/auth/invite/accept", { token, ...form });
      applySession(data);
      toast.success("Welcome to the CARENEST team");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <AuthShell title="Invitation unavailable" subtitle={error}>
        <Button className="w-full rounded-full bg-emerald-900" onClick={() => navigate("/login")} data-testid="invite-to-login-btn">
          Go to login
        </Button>
      </AuthShell>
    );
  }

  if (!invite) {
    return (
      <AuthShell title="Checking your invitation" subtitle="One moment…">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-emerald-800" />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={`Welcome, ${invite.full_name.split(" ")[0]}`}
      subtitle={`You have been invited to join CARENEST as ${invite.role.replaceAll("_", " ").toLowerCase()}. Set your password to activate your account.`}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="accept-invite-form">
        <div>
          <Label>Email</Label>
          <Input value={invite.email} disabled className="mt-1.5" data-testid="invite-email-display" />
        </div>
        <div>
          <Label htmlFor="inv-phone">Phone number</Label>
          <Input
            id="inv-phone"
            required
            placeholder="+2348030000000"
            className="mt-1.5"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            data-testid="invite-phone-input"
          />
        </div>
        <div>
          <Label htmlFor="inv-password">Choose a password</Label>
          <PasswordInput
            id="inv-password"
            required
            minLength={8}
            className="mt-1.5"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            testId="invite-password-input"
          />
          <p className="mt-1 text-xs text-slate-500">At least 8 characters.</p>
        </div>
        <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-900 py-6 hover:bg-emerald-800" data-testid="invite-accept-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Activate my account"}
        </Button>
      </form>
    </AuthShell>
  );
}
